/** Selector de cliente/proveedor con alta rápida, y formulario de tercero. */
import { useState } from 'react';
import { Button, Checkbox, Group, Modal, Select, SimpleGrid, Stack, Textarea, TextInput } from '@mantine/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { avisarError, mostrarAvisos } from '../comun';
import type { Tercero } from '../tipos';

export const OPCIONES_TIPO_ID = [
  { value: 'NIF', label: 'NIF / NIE español' },
  { value: 'NIF_IVA', label: 'NIF-IVA de otro país UE' },
  { value: 'PASAPORTE', label: 'Pasaporte' },
  { value: 'DOC_PAIS_RESIDENCIA', label: 'Documento del país de residencia' },
  { value: 'OTRO', label: 'Otro documento' },
];

export const OPCIONES_ESTABLECIDO = [
  { value: 'si', label: 'Sí, está en Canarias' },
  { value: 'no', label: 'No (Península, extranjero…)' },
  { value: 'desconocido', label: 'No lo sé' },
];

type Datos = Omit<Tercero, 'id' | 'identificacionValida' | 'archivado'>;

export const terceroVacio = (tipo: 'cliente' | 'proveedor'): Datos => ({
  esCliente: tipo === 'cliente',
  esProveedor: tipo === 'proveedor',
  nombre: '',
  nombreComercial: null,
  tipoIdentificacion: 'NIF',
  pais: 'ES',
  identificacion: null,
  establecidoCanarias: 'desconocido',
  email: null,
  direccion: null,
  notas: null,
});

export function FormularioTercero({ inicial, onGuardado, onCancelar }: { inicial: Datos & { id?: string }; onGuardado: (t: Tercero) => void; onCancelar: () => void }) {
  const [d, setD] = useState(inicial);
  const [enviando, setEnviando] = useState(false);
  const qc = useQueryClient();
  const texto = (k: keyof Datos) => ({
    value: (d[k] as string | null) ?? '',
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setD({ ...d, [k]: e.currentTarget.value || null }),
  });
  const guardar = async () => {
    setEnviando(true);
    try {
      const cuerpo = { ...d, nombre: d.nombre.trim(), email: d.email || null };
      const t = inicial.id ? await api.put<Tercero>(`/terceros/${inicial.id}`, cuerpo) : await api.post<Tercero>('/terceros', cuerpo);
      mostrarAvisos([t.aviso]);
      await qc.invalidateQueries({ queryKey: ['terceros'] });
      onGuardado(t);
    } catch (e) {
      avisarError(e);
    } finally {
      setEnviando(false);
    }
  };
  return (
    <Stack>
      <TextInput label="Nombre o razón social" required value={d.nombre} onChange={(e) => setD({ ...d, nombre: e.currentTarget.value })} data-autofocus />
      <TextInput label="Nombre comercial" {...texto('nombreComercial')} />
      <SimpleGrid cols={{ base: 1, xs: 2 }}>
        <Select
          label="Tipo de identificación"
          data={OPCIONES_TIPO_ID}
          value={d.tipoIdentificacion}
          onChange={(v) => setD({ ...d, tipoIdentificacion: v ?? 'NIF' })}
          allowDeselect={false}
        />
        <TextInput label="NIF / identificación" {...texto('identificacion')} />
      </SimpleGrid>
      <SimpleGrid cols={{ base: 1, xs: 2 }}>
        <TextInput label="País (código)" maxLength={2} value={d.pais} onChange={(e) => setD({ ...d, pais: e.currentTarget.value.toUpperCase() })} />
        <Select
          label="¿Establecido en Canarias?"
          description="Importa para el IGIC (posible modelo 412)"
          data={OPCIONES_ESTABLECIDO}
          value={d.establecidoCanarias}
          onChange={(v) => setD({ ...d, establecidoCanarias: (v ?? 'desconocido') as Datos['establecidoCanarias'] })}
          allowDeselect={false}
        />
      </SimpleGrid>
      <Group>
        <Checkbox label="Cliente" checked={d.esCliente} onChange={(e) => setD({ ...d, esCliente: e.currentTarget.checked })} />
        <Checkbox label="Proveedor" checked={d.esProveedor} onChange={(e) => setD({ ...d, esProveedor: e.currentTarget.checked })} />
      </Group>
      <TextInput label="Email" type="email" {...texto('email')} />
      <TextInput label="Dirección" {...texto('direccion')} />
      <Textarea label="Notas" autosize minRows={2} {...texto('notas')} />
      <Group justify="flex-end">
        <Button variant="default" onClick={onCancelar}>
          Cancelar
        </Button>
        <Button onClick={guardar} loading={enviando} disabled={!d.nombre.trim()}>
          Guardar
        </Button>
      </Group>
    </Stack>
  );
}

/** Selector con búsqueda y opción de crear un tercero nuevo sin salir del formulario. */
export function SelectorTercero({
  valor,
  onCambio,
  tipo,
  label,
  required,
}: {
  valor: string | null;
  onCambio: (id: string | null, t?: Tercero) => void;
  tipo: 'cliente' | 'proveedor';
  label: string;
  required?: boolean;
}) {
  const [nuevo, setNuevo] = useState<string | null>(null);
  const terceros = useQuery({ queryKey: ['terceros'], queryFn: () => api.get<Tercero[]>('/terceros') });
  const lista = (terceros.data ?? []).filter((t) => (tipo === 'cliente' ? t.esCliente : t.esProveedor) || t.id === valor);
  const [busqueda, setBusqueda] = useState('');
  const datos = [
    ...lista.map((t) => ({ value: t.id, label: `${t.nombre}${t.nombreComercial ? ` (${t.nombreComercial})` : ''}${t.identificacion ? ` · ${t.identificacion}` : ''}` })),
    ...(busqueda.trim() ? [{ value: '__nuevo__', label: `+ Crear "${busqueda.trim()}"` }] : [{ value: '__nuevo__', label: '+ Crear nuevo…' }]),
  ];
  return (
    <>
      <Select
        label={label}
        required={required}
        searchable
        clearable
        data={datos}
        value={valor}
        searchValue={busqueda}
        onSearchChange={setBusqueda}
        nothingFoundMessage="Sin resultados"
        onChange={(v) => {
          if (v === '__nuevo__') {
            setNuevo(busqueda.trim());
            return;
          }
          onCambio(v, terceros.data?.find((t) => t.id === v));
        }}
      />
      <Modal opened={nuevo !== null} onClose={() => setNuevo(null)} title={tipo === 'cliente' ? 'Nuevo cliente' : 'Nuevo proveedor'} size="lg">
        {nuevo !== null && (
          <FormularioTercero
            inicial={{ ...terceroVacio(tipo), nombre: nuevo }}
            onCancelar={() => setNuevo(null)}
            onGuardado={(t) => {
              setNuevo(null);
              setBusqueda('');
              onCambio(t.id, t);
            }}
          />
        )}
      </Modal>
    </>
  );
}
